'use client';

// Live Trading Chat — lobby of rooms by instrument (owner decision 2026-09-04).
// Left: rooms grouped by market (pinned instruments listed once they have a message or someone
// online — since 2026-09-05 empty pinned rooms are hidden; any other instrument room stays
// listed forever once opened, owner decision 2026-10-01 — was hidden after 7 days idle) with
// "N online" from a shared lobby presence channel, an unread-badge dot (owner decision
// 2026-09-08, LiveRoomRead-backed), and the last message.
// Right: the selected room (ChatRoomView; symbol rooms share the LiveChatMessage table with
// roomId=null, kept forever — owner decision 2026-09-30, was a 7-day TTL, before that 24h).
// `?room=XAUUSD` selects a room, `?room=general` (or none on desktop) is the general room.
// Mobile shows one pane at a time.
// Invite links (`?join=1`) route a signed-out visitor through sign-in and back here.
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { ArrowLeft, BadgeCheck, MessageSquare, Plus, Search, Users } from 'lucide-react';
import Navbar from '@/components/Navbar';
import ChatRoomView from '@/components/chat/ChatRoomView';
import MembersDirectory from '@/components/chat/MembersDirectory';
import InviteButton from '@/components/chat/InviteButton';
import { useJoinParam } from '@/components/chat/useJoinParam';
import LiveEventBanner from '@/components/chat/LiveEventBanner';
import CommunityGate from '@/components/chat/CommunityGate';
import type { CommunityRequest } from '@/lib/community';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/lib/i18n/LanguageContext';
import { timeAgoShort } from '@/lib/dates';
import {
  canPinLiveMessages,
  chatInviteUrl,
  listLiveRooms,
  markLiveRoomRead,
  openLiveRoom,
  openTraderRoom,
  subscribeLobbyPresence,
  type LiveRoomRow,
  type PresenceUser,
} from '@/lib/liveChat';
import {
  guessMarket,
  isPinnedRoom,
  liveRoomHref,
  MARKET_LABEL,
  MARKET_ORDER,
  normalizeSymbol,
  PINNED_ROOMS,
  roomLabel,
  type LiveMarket,
} from '@/lib/liveRooms';

const ROOMS_POLL_MS = 20_000;
const CORE_ROOMS = new Set(['XAUUSD', 'EURUSD', 'GBPUSD', 'BTCUSD', 'NAS100', 'US500', 'GER40']);

type RoomItem = {
  symbol: string; // '' = general
  market: LiveMarket | null;
  pinned: boolean;
  online: number;
  msgs24h: number;
  lastText: string | null;
  lastImageUrl: string | null;
  lastUsername: string | null;
  lastAt: string | null;
  hasUnread: boolean;
  ownerId: string | null;
  ownerUsername: string | null;
  ownerDisplayName: string | null;
  ownerImage: string | null;
  ownerVerified: boolean;
};

const EMPTY_OWNER = { ownerId: null, ownerUsername: null, ownerDisplayName: null, ownerImage: null, ownerVerified: false };

function LiveLobbyInner() {
  const { t } = useLang();
  const { user, session, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Community gate (2026-09-07): /live is the fxdoctor.pro community — members only.
  const [membership, setMembership] = useState<{ member: boolean; signedIn: boolean; request: CommunityRequest | null; open?: boolean } | null>(null);
  const authHeaders = useCallback((): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (session?.access_token) h['Authorization'] = `Bearer ${session.access_token}`;
    return h;
  }, [session]);
  const loadMembership = useCallback(async () => {
    try {
      const res = await fetch('/api/community/me', { headers: authHeaders(), credentials: 'include', cache: 'no-store' });
      setMembership(res.ok ? await res.json() : { member: false, signedIn: !!user, request: null });
    } catch { setMembership({ member: false, signedIn: !!user, request: null }); }
  }, [authHeaders, user]);
  useEffect(() => { if (!authLoading) void loadMembership(); }, [authLoading, loadMembership]);
  const isMember = membership?.member === true;

  // Selected room: '' = general; null = nothing selected (mobile list view / desktop default general).
  const roomParam = searchParams?.get('room');
  const selected: string | null = roomParam == null ? null : roomParam === 'general' ? '' : (normalizeSymbol(roomParam) ?? '');
  const activeRoom = selected ?? ''; // what the right pane shows on desktop
  // "Svi članovi" — full member roster, a sibling view to the room list/chat (?tab=members).
  const membersView = searchParams?.get('tab') === 'members';

  useJoinParam(t('You are now in the live chat!'));

  // Clear the unread badge for whichever room is actually open — covers direct links/back-forward
  // too, not just the sidebar click (go() also marks read, so switching rooms is instant).
  useEffect(() => {
    if (user && selected != null) void markLiveRoomRead(selected || null);
  }, [user, selected]);

  const [rows, setRows] = useState<LiveRoomRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [online, setOnline] = useState(0);
  const [users, setUsers] = useState<PresenceUser[]>([]);
  const [query, setQuery] = useState('');
  const [openError, setOpenError] = useState('');
  const [opening, setOpening] = useState(false);
  const presence = useRef<ReturnType<typeof subscribeLobbyPresence> | null>(null);

  const loadRooms = useCallback(async () => {
    try {
      if (!isMember) return;
      setRows(await listLiveRooms());
    } catch {
      /* keep the previous list */
    }
  }, [isMember]);

  useEffect(() => {
    void loadRooms();
    const id = setInterval(loadRooms, ROOMS_POLL_MS);
    return () => clearInterval(id);
  }, [loadRooms]);

  // Shared lobby presence — one channel for everyone on /live; each visitor announces their room.
  useEffect(() => {
    const key = user?.id ?? `guest-${Math.random().toString(36).slice(2, 10)}`;
    presence.current = subscribeLobbyPresence({ presenceKey: key, room: activeRoom, onCounts: setCounts });
    return () => {
      presence.current?.unsubscribe();
      presence.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  useEffect(() => {
    presence.current?.setRoom(activeRoom);
  }, [activeRoom]);

  // Merge pinned rooms + active rooms from the DB + live counts into one list per market.
  const groups = useMemo(() => {
    const bySymbol = new Map<string, RoomItem>();
    for (const p of PINNED_ROOMS) {
      bySymbol.set(p.symbol, { symbol: p.symbol, market: p.market, pinned: true, online: 0, msgs24h: 0, lastText: null, lastImageUrl: null, lastUsername: null, lastAt: null, hasUnread: false, ...EMPTY_OWNER });
    }
    let general: RoomItem = { symbol: '', market: null, pinned: true, online: 0, msgs24h: 0, lastText: null, lastImageUrl: null, lastUsername: null, lastAt: null, hasUnread: false, ...EMPTY_OWNER };
    for (const r of rows) {
      const stats = { msgs24h: r.msgs24h, lastText: r.lastText, lastImageUrl: r.lastImageUrl, lastUsername: r.lastUsername, lastAt: r.lastAt, hasUnread: r.hasUnread };
      const owner = { ownerId: r.ownerId, ownerUsername: r.ownerUsername, ownerDisplayName: r.ownerDisplayName, ownerImage: r.ownerImage, ownerVerified: r.ownerVerified };
      if (r.symbol == null) { general = { ...general, ...stats }; continue; }
      const sym = normalizeSymbol(r.symbol);
      if (!sym) continue;
      const existing = bySymbol.get(sym);
      const market = (existing?.market ?? (r.market as LiveMarket | null) ?? guessMarket(sym)) as LiveMarket;
      bySymbol.set(sym, { ...(existing ?? { symbol: sym, market, pinned: isPinnedRoom(sym), online: 0, ...EMPTY_OWNER }), ...stats, ...owner, market });
    }
    // The room currently open (typed by the visitor) is listed even before its first message.
    if (selected && !bySymbol.has(selected)) {
      bySymbol.set(selected, { symbol: selected, market: guessMarket(selected), pinned: false, online: 0, msgs24h: 0, lastText: null, lastImageUrl: null, lastUsername: null, lastAt: null, hasUnread: false, ...EMPTY_OWNER });
    }
    for (const item of bySymbol.values()) item.online = counts[item.symbol] ?? 0;
    general.online = counts[''] ?? 0;
    // (2026-09-06) The core rooms are always listed (a lobby with one room looked broken); other
    // pinned instruments appear once someone is online or has posted. Type a symbol to open any.
    for (const [sym, item] of [...bySymbol.entries()]) {
      if (item.pinned && !CORE_ROOMS.has(sym) && item.online === 0 && !item.lastAt && sym !== selected) bySymbol.delete(sym);
    }

    const pinnedIndex = new Map(PINNED_ROOMS.map((p, i) => [p.symbol, i]));
    const out: { market: LiveMarket; rooms: RoomItem[] }[] = [];
    for (const m of MARKET_ORDER) {
      const rooms = [...bySymbol.values()]
        .filter((r) => r.market === m)
        .sort((a, b) =>
          b.online - a.online ||
          (b.lastAt ? Date.parse(b.lastAt) : 0) - (a.lastAt ? Date.parse(a.lastAt) : 0) ||
          (pinnedIndex.get(a.symbol) ?? 999) - (pinnedIndex.get(b.symbol) ?? 999) ||
          a.symbol.localeCompare(b.symbol),
        );
      if (rooms.length) out.push({ market: m, rooms });
    }
    return { general, groups: out };
  }, [rows, counts, selected]);

  const filter = query.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  const visibleGroups = useMemo(
    () => (filter ? groups.groups.map((g) => ({ ...g, rooms: g.rooms.filter((r) => r.symbol.includes(filter) || (roomLabel(r.symbol) ?? '').toUpperCase().includes(filter)) })).filter((g) => g.rooms.length) : groups.groups),
    [groups, filter],
  );
  const filterIsNewRoom = !!filter && normalizeSymbol(filter) !== null && !visibleGroups.some((g) => g.rooms.some((r) => r.symbol === filter));

  const go = useCallback((symbol: string) => {
    setQuery('');
    setOpenError('');
    router.push(liveRoomHref(symbol || null));
  }, [router]);

  const onOpenRoom = useCallback(async () => {
    const sym = normalizeSymbol(query);
    if (!sym) {
      setOpenError(t('Enter an instrument like XAUUSD, EURUSD or BTCUSD.'));
      return;
    }
    if (!user) {
      router.push(`/sign-up-login-screen?next=${encodeURIComponent(liveRoomHref(sym))}`);
      return;
    }
    setOpening(true);
    try {
      await openLiveRoom(sym, guessMarket(sym));
      await loadRooms();
      go(sym);
    } catch (e: any) {
      setOpenError(e?.message === 'rate_limited' ? t('Too many rooms opened — try again in a bit.') : t('Enter an instrument like XAUUSD, EURUSD or BTCUSD.'));
    } finally {
      setOpening(false);
    }
  }, [query, user, router, loadRooms, go, t]);

  const named = users.filter((u) => u.username);
  const activeRoomItem = useMemo(() => {
    if (!activeRoom) return null;
    for (const g of groups.groups) {
      const found = g.rooms.find((r) => r.symbol === activeRoom);
      if (found) return found;
    }
    return null;
  }, [groups, activeRoom]);
  const isTraderRoom = activeRoomItem?.market === 'TRADER';
  const title = activeRoom ? (isTraderRoom ? activeRoomItem?.ownerDisplayName || `@${activeRoomItem?.ownerUsername}` : activeRoom) : t('General chat');
  const subtitle = activeRoom
    ? isTraderRoom
      ? `@${activeRoomItem?.ownerUsername} · ${t('Verified trader room')}`
      : roomLabel(activeRoom) ? `${t(roomLabel(activeRoom)!)} · ${t('Live trading')}` : t('Live trading')
    : t('Everything that is not one instrument.');
  const showChatOnMobile = selected != null || membersView;

  // Verified traders get one personal room, keyed by their username (owner request, 2026-10-01).
  const [canOpenTraderRoom, setCanOpenTraderRoom] = useState(false);
  const [openingTraderRoom, setOpeningTraderRoom] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!user) { setCanOpenTraderRoom(false); return; }
    void canPinLiveMessages().then((ok) => { if (alive) setCanOpenTraderRoom(ok); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);
  const myTraderRoom = useMemo(() => {
    if (!user) return null;
    for (const g of groups.groups) {
      const found = g.rooms.find((r) => r.market === 'TRADER' && r.ownerId === user.id);
      if (found) return found;
    }
    return null;
  }, [groups, user]);
  const onOpenTraderRoom = useCallback(async () => {
    setOpeningTraderRoom(true);
    try {
      const sym = await openTraderRoom();
      await loadRooms();
      go(sym);
    } catch {
      /* best-effort — verification already gates the button */
    } finally {
      setOpeningTraderRoom(false);
    }
  }, [loadRooms, go]);

  const roomRow = (r: RoomItem) => {
    const active = !membersView && r.symbol === activeRoom && selected != null;
    const isActiveDesktop = !membersView && r.symbol === activeRoom;
    const label = r.symbol ? roomLabel(r.symbol) : null;
    const isTrader = r.market === 'TRADER';
    // The room you're currently looking at never shows unread — avoids it flashing back on
    // while you're actively viewing it (the read-marking effect settles a beat behind polling).
    const unread = r.hasUnread && !isActiveDesktop;
    return (
      <button
        key={r.symbol || 'general'}
        onClick={() => go(r.symbol)}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150"
        style={{ background: isActiveDesktop ? 'var(--bg-sunken)' : 'transparent' }}
        onMouseEnter={(e) => { if (!isActiveDesktop) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; }}
        onMouseLeave={(e) => { if (!isActiveDesktop) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
        aria-current={active ? 'page' : undefined}
      >
        <div className="relative shrink-0">
          {isTrader && r.ownerImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={r.ownerImage} alt={r.ownerUsername ?? r.symbol} className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center font-mono-nums font-bold text-[11px]"
              style={{ background: isActiveDesktop ? 'var(--accent)' : 'var(--accent-muted)', color: isActiveDesktop ? '#fff' : 'var(--accent)' }}
            >
              {isTrader ? (r.ownerUsername?.[0]?.toUpperCase() ?? '#') : r.symbol ? r.symbol.slice(0, 3) : '#'}
            </div>
          )}
          {unread && (
            <span
              className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full"
              style={{ background: 'var(--accent)', border: '2px solid var(--bg-card)' }}
              aria-label={t('Unread messages')}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm truncate inline-flex items-center gap-1" style={{ color: 'var(--text-primary)', fontWeight: unread ? 800 : 700 }}>
              {isTrader ? (r.ownerDisplayName || `@${r.ownerUsername}`) : r.symbol ? r.symbol : t('General')}
              {isTrader && r.ownerVerified ? <BadgeCheck size={13} style={{ color: 'var(--accent)' }} /> : null}
              {label ? <span className="font-normal" style={{ color: 'var(--text-muted)' }}> · {t(label)}</span> : null}
            </span>
            {r.online > 0 ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold font-mono-nums shrink-0" style={{ color: 'var(--bullish)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--bullish)' }} />
                {r.online}
              </span>
            ) : r.lastAt ? (
              <span className="text-[11px] font-mono-nums shrink-0" style={{ color: unread ? 'var(--accent)' : 'var(--text-muted)', fontWeight: unread ? 700 : 400 }}>{timeAgoShort(r.lastAt)}</span>
            ) : null}
          </div>
          <p className="text-xs truncate" style={{ color: unread ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: unread ? 600 : 400 }}>
            {r.lastText
              ? `${r.lastUsername ? `${r.lastUsername}: ` : ''}${r.lastText}`
              : r.lastImageUrl
                ? `${r.lastUsername ? `${r.lastUsername}: ` : ''}📈 ${t('chart')}`
                : t('No recent messages')}
          </p>
        </div>
      </button>
    );
  };

  const roomsPane = (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 pt-3 pb-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="font-heading" style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 800 }}>{t('Live Trading Chat')}</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Rooms by instrument — chat while you trade.')}</p>
          </div>
          <Link href="/messages" className="p-2 rounded-lg shrink-0" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }} title={t('Messages')}>
            <MessageSquare size={16} />
          </Link>
        </div>
        <form
          className="mt-3 flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); void onOpenRoom(); }}
        >
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpenError(''); }}
              placeholder={t('Instrument, e.g. XAUUSD')}
              maxLength={12}
              className="input-base w-full pl-9 pr-3 py-2 text-sm uppercase"
              aria-label={t('Open a room')}
            />
          </div>
          <button
            type="submit"
            disabled={opening || !filter}
            className="btn-primary px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-40"
            title={t('Open a room')}
          >
            <Plus size={14} /> {t('Open')}
          </button>
        </form>
        {openError ? <p className="text-xs mt-1.5" style={{ color: 'var(--bearish)' }}>{openError}</p> : null}
        {canOpenTraderRoom && !myTraderRoom ? (
          <button
            onClick={() => void onOpenTraderRoom()}
            disabled={openingTraderRoom}
            className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-bold disabled:opacity-40"
            style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}
          >
            <BadgeCheck size={14} /> {t('Open your own live room')}
          </button>
        ) : null}
      </div>

      {!filter ? (
        <button
          onClick={() => { setQuery(''); setOpenError(''); router.push('/live?tab=members'); }}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-left border-b transition-colors duration-150"
          style={{ borderColor: 'var(--border)', background: membersView ? 'var(--bg-sunken)' : 'transparent' }}
          onMouseEnter={(e) => { if (!membersView) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)'; }}
          onMouseLeave={(e) => { if (!membersView) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
          aria-current={membersView ? 'page' : undefined}
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: membersView ? 'var(--accent)' : 'var(--accent-muted)', color: membersView ? '#fff' : 'var(--accent)' }}
          >
            <Users size={17} />
          </div>
          <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('All members')}</span>
        </button>
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto pb-2">
        {!filter ? (
          <>
            <div className="px-4 pt-3 pb-1 text-[11px] font-extrabold uppercase" style={{ color: 'var(--text-muted)', letterSpacing: '1.2px' }}>{t('General')}</div>
            {roomRow(groups.general)}
          </>
        ) : null}
        {visibleGroups.map((g) => (
          <div key={g.market}>
            <div className="px-4 pt-3 pb-1 text-[11px] font-extrabold uppercase" style={{ color: 'var(--text-muted)', letterSpacing: '1.2px' }}>{t(MARKET_LABEL[g.market])}</div>
            {g.rooms.map(roomRow)}
          </div>
        ))}
        {filterIsNewRoom ? (
          <div className="px-4 py-4">
            <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{t('No recent messages')} · {filter}</p>
            <button onClick={() => void onOpenRoom()} disabled={opening} className="btn-primary w-full py-2 text-sm flex items-center justify-center gap-1 disabled:opacity-40">
              <Plus size={14} /> {t('Open a room')} {filter}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh flex flex-col" style={{ background: 'var(--bg-base)' }}>
      <Navbar />
      {/* Same shell as Feed / Forum / Premium: 1280px container, 272px left card, main card. */}
      <div className="mx-auto w-full flex items-start md:gap-0" style={{ maxWidth: 1280 }}>
        {!isMember ? (
          <main className="flex-1 min-w-0 flex flex-col pb-16 md:pb-6">
            <div className="md:mx-6 md:mt-6 md:rounded-[18px] md:border flex flex-col overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', minHeight: 'calc(100dvh - 64px - 48px)' }}>
              {membership == null ? (
                <div className="flex-1 flex items-center justify-center py-24"><div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>
              ) : membership.open && !membership.signedIn ? (
                // /live is open to every registered user (owner, 2026-09-23) — guests just need an account.
                <div className="flex-1 flex flex-col items-center justify-center text-center px-6 py-20">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--accent-muted)' }}>
                    <MessageSquare size={26} style={{ color: 'var(--accent)' }} />
                  </div>
                  <h1 className="text-xl font-extrabold" style={{ color: 'var(--text-primary)' }}>{t('Live chat is open to everyone')}</h1>
                  <p className="text-sm mt-2 max-w-[420px]" style={{ color: 'var(--text-secondary)' }}>{t('Create a free ShowMeTrade account to join the rooms, chat with traders and comment in real time.')}</p>
                  <div className="flex flex-wrap gap-2 justify-center mt-5">
                    <Link href="/sign-up-login-screen?mode=signup&next=%2Flive" className="btn-primary px-5 py-2.5 text-sm">{t('Sign up free')}</Link>
                    <Link href="/sign-up-login-screen?next=%2Flive" className="btn-ghost px-5 py-2.5 text-sm">{t('Sign in')}</Link>
                  </div>
                </div>
              ) : (
                <CommunityGate signedIn={membership.signedIn} request={membership.request} authHeaders={authHeaders} onSubmitted={loadMembership} />
              )}
            </div>
          </main>
        ) : (<>
        {/* Rooms card (left column) */}
        <aside
          className={`${showChatOnMobile ? 'hidden md:flex' : 'flex'} w-full md:w-[300px] shrink-0 flex-col md:sticky`}
          style={{ padding: '0 0 24px', top: 64 }}
        >
          <div className="md:ml-6 md:mt-6 md:rounded-[18px] md:border flex flex-col overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', height: 'calc(100dvh - 64px - 48px)', minHeight: 480 }}>
            {roomsPane}
          </div>
        </aside>

        {/* Chat card (main column) */}
        <main className={`${showChatOnMobile ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 flex-col pb-16 md:pb-6`} style={{ padding: '0 0 64px' }}>
          <div className="md:mx-6 md:mt-6 md:rounded-[18px] md:border flex flex-col overflow-hidden" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', height: 'calc(100dvh - 64px - 48px)', minHeight: 480 }}>
            {membersView ? (
              <>
                <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                  <button className="md:hidden shrink-0" onClick={() => router.push('/live')} title={t('Back to rooms')}>
                    <ArrowLeft size={18} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                  <div className="min-w-0">
                    <h2 className="font-heading font-bold truncate" style={{ color: 'var(--text-primary)' }}>{t('All members')}</h2>
                    <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{t('Everyone in the FX Doctor community.')}</p>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <MembersDirectory />
                </div>
              </>
            ) : (<>
            <LiveEventBanner currentRoom={activeRoom} />
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <button className="md:hidden shrink-0" onClick={() => router.push('/live')} title={t('Back to rooms')}>
                  <ArrowLeft size={18} style={{ color: 'var(--text-secondary)' }} />
                </button>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-heading font-bold truncate" style={{ color: 'var(--text-primary)' }}>
                      {activeRoom ? <span className="font-mono-nums tracking-wider">{title}</span> : title}
                    </h2>
                    {online > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-bold font-mono-nums" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: 'var(--bullish)' }} />
                          <span className="relative inline-flex w-1.5 h-1.5 rounded-full" style={{ background: 'var(--bullish)' }} />
                        </span>
                        {online} {t('online')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>
                </div>
              </div>
              <div className="shrink-0">
                <InviteButton
                  getUrl={() => chatInviteUrl({ symbol: activeRoom || null })}
                  message={activeRoom ? `${t('Join the live chat for')} ${activeRoom} ${t('on ShowMeTrade')}` : t('Join me in the live trading chat on ShowMeTrade')}
                  note={t('Only works for people already approved as Zajednica members — anyone else will see the request-access page instead.')}
                />
              </div>
            </div>

            {named.length > 0 ? (
              <div className="flex items-center gap-2 overflow-x-auto px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                {named.map((u) => {
                  // Presence meta is client-authored — only load avatars from our own storage.
                  const image = u.image && u.image.startsWith(process.env.NEXT_PUBLIC_SUPABASE_URL ?? ' ') ? u.image : null;
                  return (
                    <div key={u.key} className="flex items-center gap-1.5 shrink-0 rounded-full py-1 pl-1 pr-2.5 border" style={{ background: 'var(--bg-sunken)', borderColor: 'var(--border)' }}>
                      {image ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={image} alt={u.username} className="w-[22px] h-[22px] rounded-full object-cover" />
                      ) : (
                        <div className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[10px] font-bold" style={{ background: 'var(--accent-muted)', color: 'var(--accent)' }}>
                          {u.username?.[0]?.toUpperCase() ?? '?'}
                        </div>
                      )}
                      <span className="text-xs truncate max-w-[110px]" style={{ color: 'var(--text-secondary)' }}>{u.username}</span>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--bullish)' }} />
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div className="flex-1 min-h-0">
              <ChatRoomView
                key={activeRoom || 'general'}
                roomId={null}
                symbol={activeRoom || null}
                onPresence={setOnline}
                onPresenceUsers={setUsers}
                emptyTitle={activeRoom ? `${t('No chat yet for')} ${activeRoom}` : t('No messages yet')}
                emptySub={activeRoom ? t('Share a setup or a chart — the session starts here.') : t('Say something — the session starts here.')}
              />
            </div>
            </>)}
          </div>
        </main>
        </>)}
      </div>
    </div>
  );
}

export default function LiveChatPage() {
  return (
    <Suspense fallback={<div className="h-dvh" style={{ background: 'var(--bg-base)' }} />}>
      <LiveLobbyInner />
    </Suspense>
  );
}
